/**
 * GuestProfile — faithful port of the canonical guest profile screen.
 * 2-col grid: left (profile card + tab card) | right 360px (booking responses, segments, connected systems)
 * Modals: send message · invite to event · edit profile · add to segment
 */
import { useState, useMemo, useEffect, type ReactNode } from "react";
import { useTenantLabels } from "../../lib/labels";
import { alpha, Avatar, Btn, Card, Icon, IconBtn, Input, MoreMenu, Pill, Tab, T, tx } from "@v0y4ge/jrni-ui";
import BookAppointmentSheet from "../appointments/BookAppointmentSheet";
import { Screen } from "../../shell/chrome";
import { can } from "../../shell/access";
import { toast } from "../../shell/toast";
import { useNav } from "../../shell/router";
import { createGuestNote, listGuestNotes, listGuests } from "@v0y4ge/api-client";
import { useGuests, type Guest } from "./useGuests";
import { useEvents } from "../events/useEvents";
import { logUpsert } from "../../lib/audit";
import { useT, t as i18nT } from "../../lib/i18n";

/** Shared shape for the editable profile summary (name/contact + VIP/press flags). */
interface ProfileState { name: string; email: string; phone?: string; company?: string; title?: string; vip?: boolean; press?: boolean; }

/* ── Helpers ──────────────────────────────────────────────────────── */
function ProfileStat({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <div style={{ fontSize: 11.5, color: T.soft, fontWeight: 600, textTransform: "uppercase" as const, letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 14, color: T.n1, fontWeight: 700 }}>{value}</div>
    </div>
  );
}
function KV({ k, v }: { k: string; v: ReactNode }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, fontSize: 13 }}>
      <span style={{ color: T.soft, flexShrink: 0 }}>{k}</span>
      <span style={{ color: T.n1, fontWeight: 500, textAlign: "right" as const }}>{v}</span>
    </div>
  );
}
function SyncRow({ brand, name, id, synced, onClick }: { brand: string; name: string; id: string; synced: string; onClick?: () => void }) {
  const t = useT();
  return (
    <div onClick={onClick} style={{ display: "flex", gap: 10, alignItems: "center", padding: "8px 0", cursor: onClick ? "pointer" : "default" }}>
      <span style={{ width: 28, height: 28, borderRadius: 6, background: brand, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontWeight: 800, fontSize: 11, color: "#fff" }}>{name[0]}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: T.n1, fontWeight: 600 }}>{name}</div>
        <div style={{ fontSize: 11.5, color: T.soft }}>{id} {t("guests.profile.synced", "· synced")}{" "}{synced}</div>
      </div>
      <Icon.chev size={12} dir="right" style={{ color: T.soft }} />
    </div>
  );
}

/* ── Tab bodies ───────────────────────────────────────────────────── */
function TimelineTab({ guestId }: { guestId: string }) {
  const t = useT();
  const [items, setItems] = useState<{i: ReactNode; c: string; t: string; s: string}[]>([]);
  useEffect(() => {
    // listGuestActivity when available — show empty state until then
    setItems([]);
  }, [guestId]);
  if (!items.length) return <div style={{ padding: 28, textAlign: "center", color: T.soft, fontSize: 13 }}>{t("guests.profile.activityWillAppearHereOnceWiredToTheApi", "Activity will appear here once wired to the API.")}</div>;
  return (
    <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
      {items.map((it, i) => (
        <li key={i} style={{ padding: "12px 20px", borderTop: i ? `1px solid ${T.border}` : "none", display: "flex", gap: 12, alignItems: "flex-start" }}>
          <span style={{ width: 24, height: 24, borderRadius: 999, background: alpha(it.c, "18"), color: it.c, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>{it.i}</span>
          <div><div style={{ fontSize: 13, color: T.n1 }}>{it.t}</div><div style={{ fontSize: 11.5, color: T.soft, marginTop: 2 }}>{it.s}</div></div>
        </li>
      ))}
    </ul>
  );
}
function EventsTab({ nav, guestId }: { nav: ReturnType<typeof useNav>; guestId: string }) {
  const t = useT();
  const [events, setEvents] = useState<{id:string;title:string;date:string;hue:string;role:string;tone:"success"|"neutral"|"warn"}[]>([]);
  useEffect(() => {
    // listGuestEvents(guestId) when available — show empty state until then
    setEvents([]);
  }, [guestId]);
  if (!events.length) return <div style={{ padding: 28, textAlign: "center", color: T.soft, fontSize: 13 }}>{t("guests.profile.eventHistoryWillAppearOnceWiredToTheApi", "Event history will appear once wired to the API.")}</div>;
  return (
    <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
      {events.map((e, i) => (
        <li key={e.id} onClick={() => nav.go("event.detail", { id: e.id })} style={{ padding: "14px 20px", borderTop: i ? `1px solid ${T.border}` : "none", display: "flex", gap: 12, alignItems: "center", cursor: "pointer" }}>
          <span style={{ width: 36, height: 36, borderRadius: 6, background: e.hue, color: "#fff", fontWeight: 700, fontSize: 12, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{e.title.split(" ").map((w) => w[0]).slice(0, 2).join("")}</span>
          <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: 14, fontWeight: 600, color: T.n1 }}>{e.title}</div><div style={{ fontSize: 12, color: T.soft, marginTop: 2 }}>{e.date}</div></div>
          <Pill tone={e.tone}>{e.role}</Pill>
        </li>
      ))}
    </ul>
  );
}
function CommsTab({ nav, guestId }: { nav: ReturnType<typeof useNav>; guestId: string }) {
  const t = useT();
  const [rows, setRows] = useState<{ch:string;subject:string;when:string;status:string;tone:"success"|"info"|"warn"}[]>([]);
  useEffect(() => {
    // listGuestComms(guestId) when available — show empty state until then
    setRows([]);
  }, [guestId]);
  if (!rows.length) return <div style={{ padding: 28, textAlign: "center", color: T.soft, fontSize: 13 }}>{t("guests.profile.commsHistoryWillAppearOnceWiredToTheApi", "Comms history will appear once wired to the API.")}</div>;
  return (
    <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
      {rows.map((r, i) => (
        <li key={i} style={{ padding: "12px 20px", borderTop: i ? `1px solid ${T.border}` : "none", display: "flex", gap: 12, alignItems: "center" }}>
          <span style={{ width: 28, height: 28, borderRadius: 6, background: r.ch === "sms" ? alpha(T.green1, "18") : T.p4, color: r.ch === "sms" ? T.green1 : T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            {r.ch === "sms" ? <Icon.msg size={14} /> : <Icon.mail size={14} />}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>{r.subject}</div>
            <div style={{ fontSize: 12, color: T.soft, marginTop: 2 }}>{r.when}</div>
          </div>
          <Pill tone={r.tone}>{r.status}</Pill>
        </li>
      ))}
    </ul>
  );
}
function NotesTab({ notes, draft, setDraft, addNote }: { notes: { id: string; who: string; when: string; body: string }[]; draft: string; setDraft: (v: string) => void; addNote: () => void }) {
  const t = useT();
  return (
    <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
      {can("guests.write") ? (
        <div style={{ display: "flex", gap: 8 }}>
          <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={t("guests.profile.addANote", "Add a note…")} onKeyDown={(e) => e.key === "Enter" && addNote()} style={{ flex: 1 }} />
          <Btn onClick={addNote} disabled={!draft.trim()}>{t("common.actions.add", "Add")}</Btn>
        </div>
      ) : null}
      {notes.map((n) => (
        <div key={n.id} style={{ padding: "12px 14px", background: T.n6, borderRadius: 8 }}>
          <div style={{ fontSize: 13.5, color: T.n1, lineHeight: 1.5 }}>{n.body}</div>
          <div style={{ fontSize: 11.5, color: T.soft, marginTop: 6 }}>{n.who} · {n.when}</div>
        </div>
      ))}
    </div>
  );
}

/* ── Modals ─────────────────────────────────────────────────────── */
function ModalWrap({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.4)" }} onClick={onClose}>
      <div style={{ background: T.white, borderRadius: 14, padding: 28, width: 480, maxHeight: "85vh", overflow: "auto", boxShadow: T.shadowLg }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: T.n1 }}>{title}</div>
          <button onClick={onClose} style={{ border: "none", background: "transparent", color: T.n3, cursor: "pointer", display: "inline-flex" }}><Icon.x size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
function SendMsgModal({ guest, onClose }: { guest: { name: string; email: string }; onClose: () => void }) {
  const t = useT(); // i18n pilot — common.actions.* keys
  const [ch, setCh] = useState("email");
  const [body, setBody] = useState("");
  return (
    <ModalWrap title={i18nT("guests.profile.messageGuestName.icu", { value0: guest.name })} onClose={onClose}>
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        {["email","sms"].map((c) => <button key={c} onClick={() => setCh(c)} style={{ flex: 1, height: 36, borderRadius: 8, border: `1px solid ${ch === c ? T.p1 : T.border}`, background: ch === c ? T.p4 : T.white, color: ch === c ? T.p1 : T.n2, fontWeight: 600, fontFamily: T.font, fontSize: 13, cursor: "pointer", textTransform: "capitalize" as const }}>{c}</button>)}
      </div>
      <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder={t("guests.profile.writeYourMessage", "Write your message…")} rows={5} style={{ width: "100%", resize: "vertical", border: `1px solid ${T.n4}`, borderRadius: 8, padding: "10px 12px", fontFamily: T.font, fontSize: 13, color: T.n1, outline: "none", boxSizing: "border-box" }} />
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
        <Btn variant="secondary" onClick={onClose}>{t("common.actions.cancel", "Cancel")}</Btn>
        <Btn icon={<Icon.send size={14} />} onClick={() => { toast({ tone: "success", title: t("guests.profile.messageSent", "Message sent"), subtitle: guest.email }); onClose(); }}>{t("common.actions.send", "Send")}</Btn>
      </div>
    </ModalWrap>
  );
}
function EditProfileModal({ profile, setProfile, onClose }: { profile: ProfileState; setProfile: (p: ProfileState) => void; onClose: () => void }) {
  const t = useT(); // i18n pilot — common.actions.* keys
  const [form, setForm] = useState({ ...profile });
  const f = { display: "flex", flexDirection: "column" as const, gap: 5 };
  const lbl = { fontSize: 12.5, fontWeight: 600, color: T.n2 };
  return (
    <ModalWrap title={t("guests.profile.editProfile", "Edit profile")} onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div style={f}><span style={lbl}>{t("guests.profile.firstName", "First name")}</span><Input value={form.name.split(" ")[0]} onChange={(e) => setForm((x) => ({ ...x, name: e.target.value + " " + x.name.split(" ").slice(1).join(" ") }))} /></div>
          <div style={f}><span style={lbl}>{t("guests.profile.lastName", "Last name")}</span><Input value={form.name.split(" ").slice(1).join(" ")} onChange={(e) => setForm((x) => ({ ...x, name: x.name.split(" ")[0] + " " + e.target.value }))} /></div>
        </div>
        <div style={f}><span style={lbl}>{t("guests.profile.email", "Email")}</span><Input value={form.email} onChange={(e) => setForm((x) => ({ ...x, email: e.target.value }))} type="email" /></div>
        <div style={f}><span style={lbl}>{t("guests.profile.phone", "Phone")}</span><Input value={form.phone ?? ""} onChange={(e) => setForm((x) => ({ ...x, phone: e.target.value }))} /></div>
        <div style={f}><span style={lbl}>{t("guests.profile.company", "Company")}</span><Input value={form.company ?? ""} onChange={(e) => setForm((x) => ({ ...x, company: e.target.value }))} /></div>
        <div style={f}><span style={lbl}>{t("guests.profile.title", "Title")}</span><Input value={form.title ?? ""} onChange={(e) => setForm((x) => ({ ...x, title: e.target.value }))} /></div>
      </div>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
        <Btn variant="secondary" onClick={onClose}>{t("common.actions.cancel", "Cancel")}</Btn>
        <Btn onClick={() => { setProfile(form); toast({ tone: "success", title: t("guests.profile.profileUpdated", "Profile updated") }); onClose(); }}>{t("common.actions.save")}</Btn>
      </div>
    </ModalWrap>
  );
}
function AddSegmentModal({ current, onAdd, onClose }: { current: string[]; onAdd: (s: string) => void; onClose: () => void }) {
  const t = useT();
  const ALL_SEGS = ["VIP","Press list",t("guests.profile.earlyBird", "Early bird"),t("guests.profile.alumni", "Alumni"),t("guests.profile.newsletter", "Newsletter"),t("guests.profile.premium", "Premium")];
  const available = ALL_SEGS.filter((s) => !current.includes(s));
  return (
    <ModalWrap title={t("guests.profile.addToSegment", "Add to segment")} onClose={onClose}>
      {available.length === 0 ? <p style={{ color: T.soft, fontSize: 13 }}>{t("guests.profile.alreadyInAllSegments", "Already in all segments.")}</p> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {available.map((s) => (
            <button key={s} onClick={() => { onAdd(s); toast({ tone: "success", title: i18nT("guests.profile.addedToS.icu", { value0: s }) }); onClose(); }}
              style={{ padding: "10px 14px", border: `1px solid ${T.border}`, borderRadius: 8, background: T.white, fontSize: 13, fontWeight: 600, color: T.n1, cursor: "pointer", textAlign: "left" as const, fontFamily: T.font }}
              onMouseEnter={(e) => (e.currentTarget.style.background = T.n6)}
              onMouseLeave={(e) => (e.currentTarget.style.background = T.white)}>
              {s}
            </button>
          ))}
        </div>
      )}
    </ModalWrap>
  );
}

/* ═══ Main screen ════════════════════════════════════════════════════ */
export default function GuestProfile() {
  const t = useT();
  const nav = useNav();
  const { guests } = useGuests();
  const { events: allEvents } = useEvents();
  const lbl = useTenantLabels(); // #1 — tenant-overridable vocabulary

  // Real "Guests & Customers" tab total — same source GuestsList binds to
  // (GuestListResponse.total via listGuests); no badge until it resolves.
  const [totalGuests, setTotalGuests] = useState<number | undefined>(undefined);
  useEffect(() => {
    let live = true;
    listGuests({ limit: "1" } as Parameters<typeof listGuests>[0])
      .then((r) => { if (live) setTotalGuests((r as { total?: number; pagination?: { total?: number } }).total ?? (r as { pagination?: { total?: number } }).pagination?.total); })
      .catch(() => { /* keep badge absent — never fabricate */ });
    return () => { live = false; };
  }, []);
  const paramId = (nav.params?.id as string) || "g1";
  const g = useMemo(() => guests.find((x) => x.id === paramId) || guests[0], [paramId, guests]);

  const [tab, setTab] = useState("timeline");
  const [modal, setModal] = useState<"send" | "invite" | "edit" | "addSegment" | null>(null);
  // APT-12 (2026-07-26): event→appointment pipeline — book a 1:1 follow-up
  // from the guest record via the ONE shared booking sheet (canonical family).
  const [bookFollowUp, setBookFollowUp] = useState(false);
  useEffect(() => { if (nav.params?.edit) setModal("edit"); }, [nav.params?.id]);

  const [segments, setSegments] = useState<string[]>(() => g?.segments?.slice() ?? []);
  useEffect(() => { setSegments(g?.segments?.slice() ?? []); }, [g?.id]);

  const [profile, setProfile] = useState<ProfileState>(() => ({
    name: g?.name ?? "", email: g?.email ?? "", phone: g?.phone, company: g?.company,
    title: (g as { title?: string } | undefined)?.title, // §3c (2026-07-19): from the record — never a baked "Managing Director"
    vip: g?.ticket === "VIP" || (g?.segments ?? []).includes("VIP"),
    press: (g?.segments ?? []).includes("Press list") || g?.ticket === "Press",
  }));
  useEffect(() => {
    if (!g) return;
    setProfile({ name: g.name, email: g.email, phone: g.phone, company: g.company, title: (g as { title?: string }).title, vip: g.ticket === "VIP" || (g.segments ?? []).includes("VIP"), press: (g.segments ?? []).includes("Press list") || g.ticket === "Press" });
  }, [g?.id]);

  // §3c (2026-07-19): notes hydrate from + persist through the real guest-notes
  // ops — the old seeded "Imani Okafor" note fabricated guest correspondence.
  type NoteRow = { id: string; who: string; when: string; body: string };
  const asNoteRow = (n: { id: string; author: string; authorName?: string; when: string; body: string }): NoteRow => ({ id: n.id, who: n.authorName || n.author, when: n.when, body: n.body });
  const [notes, setNotes] = useState<NoteRow[]>([]);
  useEffect(() => {
    let live = true;
    if (!g?.id) return;
    setNotes([]);
    listGuestNotes(g.id)
      .then((r) => { if (live) setNotes(r.notes.map(asNoteRow)); })
      .catch(() => { /* keep empty — never fabricate */ });
    return () => { live = false; };
  }, [g?.id]);
  const [noteDraft, setNoteDraft] = useState("");
  const addNote = async () => {
    if (!noteDraft.trim() || !g?.id) return;
    try {
      const r = await createGuestNote(g.id, { body: noteDraft.trim() });
      const created = ((r as { note?: unknown }).note ?? r) as { id: string; author: string; authorName?: string; when: string; body: string };
      setNotes((n) => [asNoteRow(created), ...n]);
      setNoteDraft("");
      toast({ tone: "success", title: t("guests.profile.noteAdded", "Note added") });
    } catch {
      toast({ tone: "warn", title: t("guests.profile.couldnTAddNote", "Couldn't add note"), subtitle: t("guests.profile.theNoteWasNotSavedTryAgain", "The note was not saved — try again.") });
    }
  };

  const close = () => setModal(null);

  const moreItems = [
    { icon: <Icon.star size={14} />, label: profile.vip ? t("guests.profile.removeVipStatus", "Remove VIP status") : t("guests.profile.markAsVip", "Mark as VIP"),
      onClick: () => { const next = !profile.vip; setProfile((p) => ({ ...p, vip: next })); toast({ tone: next ? "success" : "neutral", title: next ? t("guests.profile.markedAsVip", "Marked as VIP") : t("guests.profile.vipStatusRemoved", "VIP status removed") }); } },
    { icon: <Icon.copy size={14} />, label: t("guests.profile.copyEmail", "Copy email"), onClick: () => toast({ tone: "info", title: t("guests.profile.emailCopied", "Email copied") }) },
    { icon: <Icon.download size={14} />, label: t("guests.profile.exportProfileCsv", "Export profile (CSV)"), onClick: () => toast({ tone: "info", title: t("guests.profile.exportStarted", "Export started") }) },
    "divider" as const,
    { icon: <Icon.warn size={14} />, label: t("guests.profile.unsubscribeFromAllComms", "Unsubscribe from all comms"), onClick: () => toast({ tone: "warn", title: t("guests.profile.unsubscribed", "Unsubscribed"), subtitle: i18nT("guests.profile.profileNameWonTReceiveFurtherEmailOrSms.icu", { value0: profile.name }) }) },
    { icon: <Icon.trash size={14} />, label: t("guests.profile.deleteGuest", "Delete guest"), danger: true, onClick: () => toast({ tone: "warn", title: t("guests.profile.guestDeleted", "Guest deleted"), subtitle: t("guests.profile.text30DaySoftDelete", "30-day soft delete.") }) },
  ];

  if (!g) return null;

  const init = g.name.split(" ").map((p) => p[0]).join("").toUpperCase().slice(0, 2);

  return (
    <Screen rail="guests" tabs={[
      { id: "all",      label: t("guests.profile.guestsLblCustomersCustomers.icu", { value0: lbl("customers", t("common.customers", "Customers")) }), count: totalGuests, onClick: () => nav.go("guests") },
      { id: "segments", label: t("guests.profile.segments", "Segments"),                          onClick: () => nav.go("segments") },
      { id: "imports",  label: t("guests.profile.imports", "Imports"),                           onClick: () => nav.go("guests.imports") },
    ]} activeTab="all">
      <div style={{ padding: "16px 24px 0", display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ fontSize: 13, color: T.soft, display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ cursor: "pointer" }} onClick={() => nav.go("guests")}>{t("guests.profile.guests", "Guests")}</span>
          <Icon.chev size={10} dir="right" />
          <span style={{ color: T.n1 }}>{profile.name}</span>
        </div>
      </div>
      <div style={{ flex: 1, overflow: "auto", padding: "16px 24px 24px", display: "grid", gridTemplateColumns: "1fr 360px", gap: 16, alignContent: "start" }}>
        {/* LEFT column */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Profile card */}
          <Card style={{ padding: 24, display: "flex", gap: 20, alignItems: "flex-start" }}>
            <Avatar size={72} initials={init} color={g.color ?? T.p1} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <h1 style={{ fontSize: 24, fontWeight: 700, color: T.n1, margin: 0 }}>{profile.name}</h1>
                {profile.vip  ? <Pill tone="primary"><Icon.star size={10} /> {t("guests.profile.vip", "VIP")}</Pill> : null}
                {profile.press ? <Pill tone="warn">{t("guests.profile.pressList", "Press list")}</Pill> : null}
                {g.status === "checked-in" ? <Pill tone="success" dot>{t("guests.profile.checkedIn", "Checked in")}</Pill> :
                 g.status === "waitlist"   ? <Pill tone="warn">{t("guests.profile.waitlist", "Waitlist")}</Pill> :
                 g.status === "no-show"    ? <Pill tone="danger">{t("guests.profile.noShow", "No-show")}</Pill> :
                                             <Pill tone="info">{t("guests.profile.registered", "Registered")}</Pill>}
              </div>
              <div style={{ display: "flex", gap: 16, marginTop: 8, color: T.n2, fontSize: 13, flexWrap: "wrap" }}>
                <a href={`mailto:${profile.email}`} style={{ display: "inline-flex", alignItems: "center", gap: 6, color: T.n2, textDecoration: "none" }}>
                  <Icon.mail size={13} /> {profile.email}
                </a>
                {profile.phone ? (
                  <a href={`tel:${profile.phone.replace(/[^+\d]/g, "")}`} style={{ display: "inline-flex", alignItems: "center", gap: 6, color: T.n2, textDecoration: "none" }}>
                    <Icon.phone size={13} /> {profile.phone}
                  </a>
                ) : null}
                {profile.company ? <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon.tags size={13} /> {profile.company}</span> : null}
              </div>
              <div style={{ display: "flex", gap: 24, marginTop: 16, flexWrap: "wrap" }}>
                {/* §3c (2026-07-19): no invented metrics — "—" until real profile/
                    activity ops exist (declared as guest-profile stats ask). */}
                <ProfileStat label={t("guests.profile.eventsAttended", "Events attended")} value="—" />
                <ProfileStat label={t("guests.profile.lifetimeValue", "Lifetime value")} value="—" />
                <ProfileStat label={t("guests.profile.lastSeen", "Last seen")} value="—" />
                <ProfileStat label={t("guests.profile.source", "Source")} value={g.source || "—"} />
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, flexShrink: 0 }}>
              <Btn icon={<Icon.send size={14} />} onClick={() => setModal("send")}>{t("guests.profile.sendMessage", "Send message")}</Btn>
              <Btn variant="secondary" icon={<Icon.calendar size={14} />} onClick={() => setModal("invite")}>{t("guests.profile.inviteToEvent", "Invite to event")}</Btn>
              {can("appointments.book") ? <Btn variant="secondary" icon={<Icon.clock size={14} />} onClick={() => setBookFollowUp(true)} testid="guest-book-followup">{t("guests.profile.bookFollowUp", "Book a follow-up")}</Btn> : null}
              <Btn variant="ghost" icon={<Icon.edit size={14} />} onClick={() => setModal("edit")}>{t("guests.profile.editProfile", "Edit profile")}</Btn>
              <MoreMenu label={t("guests.profile.moreActions", "More actions")} size={32} iconSize={14} width={240} header={t("guests.profile.guestActions", "Guest actions")} items={moreItems} />
            </div>
          </Card>

          {/* Tab card */}
          <Card>
            <div style={{ padding: "12px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", gap: 16 }}>
              <Tab active={tab === "timeline"} onClick={() => setTab("timeline")}>{t("guests.profile.timeline", "Timeline")}</Tab>
              <Tab active={tab === "events"}   onClick={() => setTab("events")}>{t("guests.profile.events", "Events")}</Tab>
              <Tab active={tab === "comms"}    onClick={() => setTab("comms")}>{t("guests.profile.comms", "Comms")}</Tab>
              <Tab active={tab === "notes"}    onClick={() => setTab("notes")}    count={notes.length}>{t("guests.profile.notes", "Notes")}</Tab>
            </div>
            {tab === "timeline" && <TimelineTab guestId={g.id} />}
            {tab === "events"   && <EventsTab nav={nav} guestId={g.id} />}
            {tab === "comms"    && <CommsTab nav={nav} guestId={g.id} />}
            {tab === "notes"    && <NotesTab notes={notes} draft={noteDraft} setDraft={setNoteDraft} addNote={addNote} />}
          </Card>
        </div>

        {/* RIGHT sidebar */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Booking responses */}
          <Card style={{ padding: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <div style={tx.label}>{t("guests.profile.bookingResponses", "Booking responses")}</div>
              <Btn variant="ghost" size="sm" icon={<Icon.edit size={12} />} onClick={() => nav.go("guests.responses", { id: g.id })}>{t("common.actions.edit", "Edit")}</Btn>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <KV k={t("guests.profile.dietary", "Dietary")}          v={t("guests.profile.vegetarianGlutenFree", "Vegetarian · gluten-free")} />
              <KV k={t("guests.profile.access", "Access")}           v="—" />
              <KV k={t("guests.profile.company", "Company")}          v={profile.company || "—"} />
              <KV k={t("guests.profile.pressCredential", "Press credential")} v={<span style={{ color: T.p1, cursor: "pointer", textDecoration: "underline" }} onClick={() => toast({ tone: "info", title: t("guests.profile.openingFile", "Opening file…") })}>{t("guests.profile.pressPass2026Pdf", "press-pass-2026.pdf")}</span>} />
            </div>
          </Card>

          {/* Segments */}
          <Card style={{ padding: 20 }}>
            <div style={{ ...tx.label, marginBottom: 10 }}>{t("guests.profile.segments", "Segments")}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {segments.length === 0 ? (
                <div style={{ fontSize: 12, color: T.soft, padding: "6px 2px" }}>{t("guests.profile.notInAnySegmentYet", "Not in any segment yet.")}</div>
              ) : segments.map((s) => (
                <div key={s} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", border: `1px solid ${T.border}`, borderRadius: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 999, background: s === "VIP" ? T.p1 : s === "Press list" ? "#C13584" : T.blue1 }} />
                  <span style={{ flex: 1, fontSize: 13, color: T.n1, fontWeight: 500, cursor: "pointer" }} onClick={() => nav.go("segments")}>{s}</span>
                  <button onClick={() => { setSegments((prev) => prev.filter((x) => x !== s)); toast({ tone: "neutral", title: i18nT("guests.profile.removedFromS.icu", { value0: s }) }); }}
                    style={{ background: "transparent", border: "none", cursor: "pointer", color: T.soft, padding: 4, display: "inline-flex", borderRadius: 4 }}>
                    <Icon.x size={12} />
                  </button>
                </div>
              ))}
            </div>
            <Btn variant="ghost" size="sm" icon={<Icon.plus size={12} />} onClick={() => setModal("addSegment")} style={{ marginTop: 10 }}>{t("guests.profile.addToSegment", "Add to segment")}</Btn>
          </Card>

          {/* Connected systems */}
          <Card style={{ padding: 20 }}>
            <div style={{ ...tx.label, marginBottom: 10 }}>{t("guests.profile.connectedSystems", "Connected systems")}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <SyncRow brand="#00A1E0" name={t("guests.profile.salesforce", "Salesforce")} id="0035g00000XYZ12" synced={t("guests.profile.text4MinAgo", "4 min ago")} onClick={() => nav.go("settings.integ")} />
              <SyncRow brand="#FF7A59" name={t("guests.profile.hubspot", "HubSpot")}    id="contact_8324"   synced={t("guests.profile.text2HoursAgo", "2 hours ago")}  onClick={() => nav.go("settings.integ")} />
            </div>
          </Card>
        </div>
      </div>

      {/* Modals */}
      {modal === "send"       ? <SendMsgModal guest={profile} onClose={close} /> : null}
      {modal === "invite"     ? (
        <ModalWrap title={t("guests.profile.inviteToEvent", "Invite to event")} onClose={close}>
          <p style={{ fontSize: 13, color: T.soft }}>{t("guests.profile.selectAnUpcomingEventToInvite", "Select an upcoming event to invite")}{" "}{profile.name} {t("guests.profile.to", "to.")}</p>
          {allEvents.slice(0,5).map((ev) => (
            <button key={ev.id} onClick={() => { toast({ tone: "success", title: t("guests.profile.inviteSent", "Invite sent"), subtitle: i18nT("guests.profile.profileNameEvTitle.icu", { value0: profile.name, value1: ev.title }) }); close(); }}
              style={{ display: "block", width: "100%", textAlign: "left" as const, padding: "10px 14px", margin: "6px 0", border: `1px solid ${T.border}`, borderRadius: 8, background: T.white, fontSize: 13, fontWeight: 600, color: T.n1, cursor: "pointer", fontFamily: T.font }}>
              {ev.title}
            </button>
          ))}
        </ModalWrap>
      ) : null}
      {modal === "edit"       ? <EditProfileModal profile={profile} setProfile={(next) => { logUpsert("guest", profile as unknown as Record<string, unknown>, next as unknown as Record<string, unknown>, { id: g?.id ?? paramId, name: next.name }); setProfile(next); }} onClose={close} /> : null}
      {modal === "addSegment" ? <AddSegmentModal current={segments} onAdd={(s) => setSegments((prev) => [...prev, s])} onClose={close} /> : null}
      <BookAppointmentSheet open={bookFollowUp} onClose={() => setBookFollowUp(false)} prefill={{ name: profile.name, email: profile.email, phone: profile.phone }} origin="guests" />
    </Screen>
  );
}
